import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, Plus, Trash2, ArrowRight, GraduationCap, Check, ClipboardCheck, AlertCircle, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";

const INST_COLOR = "#1B3A7B";

function useDebounced(fn, delayMs) {
    const timer = useRef();
    const fnRef = useRef(fn);
    fnRef.current = fn;
    return useCallback(
        (...args) => {
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(() => fnRef.current(...args), delayMs);
        },
        [delayMs],
    );
}

export function InstructionalTeachersPage() {
    const { cycleId } = useParams();
    const navigate = useNavigate();
    const [daId, setDaId] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [teachers, setTeachers] = useState([]);
    const [q, setQ] = useState("");
    const [cycle, setCycle] = useState(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data: cyc } = await api.get(`/cycles/${cycleId}`);
            setCycle(cyc);
            const inst = cyc.domain_assessments.find((d) => d.domain?.key === "instructional");
            if (!inst) {
                setError("Instructional Core domain not found in this cycle");
                return;
            }
            setDaId(inst.id);
            const { data: tas } = await api.get(`/domain-assessments/${inst.id}/teacher-assessments`);
            setRows(tas);
            setError(null);
        } catch (e) {
            setError(extractError(e, "Could not load teacher assessments"));
        } finally {
            setLoading(false);
        }
    }, [cycleId]);

    useEffect(() => {
        load();
    }, [load]);

    const openPicker = async () => {
        try {
            const { data } = await api.get("/admin/users", { params: { org_id: cycle?.org_id } });
            const alreadyAdded = new Set(rows.map((r) => r.teacher_user_id));
            setTeachers(data.filter((u) => u.role === "teacher" && !alreadyAdded.has(u.id)));
            setPickerOpen(true);
        } catch (e) {
            toast.error(extractError(e, "Could not load teachers"));
        }
    };

    const addTeacher = async (teacher) => {
        try {
            await api.post(`/domain-assessments/${daId}/teacher-assessments`, { teacher_user_id: teacher.id });
            toast.success(`Added ${teacher.full_name || teacher.email}`);
            setPickerOpen(false);
            load();
        } catch (e) {
            toast.error(extractError(e, "Could not add teacher"));
        }
    };

    const removeTeacher = async (ta) => {
        if (!window.confirm(`Remove ${ta.teacher.full_name || ta.teacher.email}?`)) return;
        try {
            await api.delete(`/teacher-assessments/${ta.id}`);
            toast.success("Removed");
            load();
        } catch (e) {
            toast.error(extractError(e, "Delete failed"));
        }
    };

    const isLocked = cycle?.status === "locked";
    const filtered = teachers.filter((t) =>
        !q ? true : (t.full_name || "").toLowerCase().includes(q.toLowerCase()) || t.email.toLowerCase().includes(q.toLowerCase()),
    );

    if (loading) return <div className="text-[#0B2545] flex items-center gap-3"><Loader2 className="animate-spin" size={18} /> Loading…</div>;
    if (error) return <div className="text-red-700">{error}</div>;

    return (
        <div className="space-y-6" data-testid="instructional-teachers-page">
            <div>
                <Link to={`/app/cycles/${cycleId}/domains/instructional`} className="text-xs text-[#1E56A0] hover:underline">
                    ← Back to Instructional Core workspace
                </Link>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg shadow-[#0B2545]/5">
                <div className="px-6 py-5 text-white flex flex-wrap justify-between gap-4 items-center" style={{ backgroundColor: INST_COLOR }}>
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.25em] font-bold opacity-90">
                            Instructional Core · teacher roster
                        </div>
                        <h1 className="mt-1 font-serif text-3xl font-semibold leading-tight">
                            Teachers in {cycle.name}
                        </h1>
                    </div>
                    <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
                        <DialogTrigger asChild>
                            <Button
                                className="rounded-full bg-white text-[#0B2545] hover:bg-[#D6E6F2]"
                                onClick={openPicker}
                                disabled={isLocked}
                                data-testid="add-teacher-btn"
                            >
                                <Plus size={14} /> Add teacher
                            </Button>
                        </DialogTrigger>
                        <DialogContent data-testid="add-teacher-dialog">
                            <DialogHeader>
                                <DialogTitle className="font-serif text-2xl text-[#0B2545]">Add teacher</DialogTitle>
                            </DialogHeader>
                            <Input
                                autoFocus
                                placeholder="Search by name or email"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                data-testid="teacher-search-input"
                            />
                            <ul className="max-h-64 overflow-y-auto mt-2 space-y-1" data-testid="teacher-picker-list">
                                {filtered.length === 0 ? (
                                    <li className="text-sm text-[#0E1B2C]/60 p-2">No teachers available.</li>
                                ) : (
                                    filtered.map((t) => (
                                        <li key={t.id}>
                                            <button
                                                className="w-full text-left rounded-md border border-[#D6E6F2] px-3 py-2 hover:bg-[#F8FBFF]"
                                                onClick={() => addTeacher(t)}
                                                data-testid={`teacher-pick-${t.id}`}
                                            >
                                                <div className="font-semibold text-[#0B2545]">{t.full_name || t.email}</div>
                                                <div className="text-xs text-[#0E1B2C]/60">{t.email}</div>
                                            </button>
                                        </li>
                                    ))
                                )}
                            </ul>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {rows.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[#8AC4FF] bg-white p-10 text-center">
                    <GraduationCap size={28} className="mx-auto text-[#1E56A0]" />
                    <p className="mt-3 text-[#0B2545] font-semibold">No teachers scored yet.</p>
                    <p className="text-sm text-[#0E1B2C]/70">Add teachers to begin per-teacher Instructional Core assessments.</p>
                </div>
            ) : (
                <div className="rounded-2xl bg-white border border-[#D6E6F2] overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm" data-testid="teacher-assessments-table">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-[#0E1B2C]/60 bg-[#F1F6FB]">
                                    <th className="px-4 py-3">Teacher</th>
                                    <th className="px-4 py-3">School</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3">Score</th>
                                    <th className="px-4 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((ta) => (
                                    <tr key={ta.id} className="border-t border-[#D6E6F2]" data-testid={`ta-row-${ta.id}`}>
                                        <td className="px-4 py-2 font-medium text-[#0B2545]">
                                            {ta.teacher.full_name || ta.teacher.email}
                                            <div className="text-xs text-[#0E1B2C]/60">{ta.teacher.email}</div>
                                        </td>
                                        <td className="px-4 py-2 text-[#0E1B2C]/80">{ta.school?.name || "—"}</td>
                                        <td className="px-4 py-2 capitalize">{ta.status.replace(/_/g, " ")}</td>
                                        <td className="px-4 py-2 font-serif">
                                            {ta.overall_rating != null ? ta.overall_rating.toFixed(2) : "—"}
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            <div className="inline-flex items-center gap-2">
                                                <Button
                                                    size="sm"
                                                    className="rounded-full text-white"
                                                    style={{ backgroundColor: INST_COLOR }}
                                                    onClick={() => navigate(`/app/cycles/${cycleId}/domains/instructional/teachers/${ta.id}`)}
                                                    data-testid={`ta-score-${ta.id}`}
                                                >
                                                    {isLocked ? "View" : "Score"}
                                                    <ArrowRight size={12} />
                                                </Button>
                                                {!isLocked && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => removeTeacher(ta)}
                                                        className="rounded-full border-red-300 text-red-700 hover:bg-red-50"
                                                        data-testid={`ta-delete-${ta.id}`}
                                                    >
                                                        <Trash2 size={12} />
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export function TeacherAssessmentScoringPage() {
    const { cycleId, teacherAssessmentId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/teacher-assessments/${teacherAssessmentId}`);
            setData(data);
            setError(null);
        } catch (e) {
            setError(extractError(e, "Could not load teacher assessment"));
        } finally {
            setLoading(false);
        }
    }, [teacherAssessmentId]);

    useEffect(() => {
        load();
    }, [load]);

    const debouncedSave = useDebounced(async (lfId, next) => {
        setSaving(true);
        try {
            await api.put(`/teacher-assessments/${teacherAssessmentId}/look-fors/${lfId}/rating`, next);
            setLastSavedAt(new Date());
        } catch (e) {
            toast.error(extractError(e, "Could not save"));
        } finally {
            setSaving(false);
        }
    }, 800);

    const updateLF = (lf, patch) => {
        const cur = { rating: lf.rating, notes: lf.notes || "" };
        const next = { ...cur, ...patch };
        setData((prev) => {
            if (!prev) return prev;
            const clone = { ...prev, indicators: prev.indicators.map((ind) => ({
                ...ind,
                look_fors: ind.look_fors.map((row) => row.id === lf.id ? { ...row, ...next } : row),
            })) };
            const indScores = clone.indicators.map((ind) => {
                const vals = ind.look_fors.map((r) => r.rating).filter((v) => v != null);
                ind.indicator_score = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
                return ind.indicator_score;
            });
            const nn = indScores.filter((v) => v != null);
            clone.overall_rating = nn.length ? nn.reduce((a, b) => a + b, 0) / nn.length : null;
            clone.rated_count = clone.indicators.reduce((n, ind) => n + ind.look_fors.filter((r) => r.rating != null).length, 0);
            return clone;
        });
        debouncedSave(lf.id, next);
    };

    const complete = async () => {
        try {
            await api.post(`/teacher-assessments/${teacherAssessmentId}/complete`);
            toast.success("Teacher assessment complete");
            load();
        } catch (e) {
            toast.error(extractError(e, "Could not complete"));
        }
    };

    if (loading) return <div className="text-[#0B2545] flex items-center gap-3"><Loader2 className="animate-spin" size={18} /> Loading…</div>;
    if (error || !data) return <div className="text-red-700">{error}</div>;

    return (
        <div className="space-y-6" data-testid="teacher-assessment-scoring-page">
            <Link to={`/app/cycles/${cycleId}/domains/instructional/teachers`} className="text-xs text-[#1E56A0] hover:underline">
                ← Back to teachers
            </Link>
            <div className="rounded-2xl overflow-hidden shadow-lg shadow-[#0B2545]/5">
                <div className="px-6 py-5 text-white flex flex-wrap justify-between items-center gap-4" style={{ backgroundColor: INST_COLOR }}>
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.25em] font-bold opacity-90">
                            Teacher assessment
                        </div>
                        <h1 className="mt-1 font-serif text-3xl font-semibold" data-testid="ta-teacher-name">
                            {data.teacher.full_name || data.teacher.email}
                        </h1>
                        <div className="text-sm text-white/80">{data.school?.name || "—"} · {data.cycle.name}</div>
                    </div>
                    <div className="text-right">
                        <div className="text-[11px] uppercase tracking-[0.25em] font-bold opacity-90">Overall</div>
                        <div className="font-serif text-3xl font-semibold" data-testid="ta-overall">
                            {data.overall_rating != null ? data.overall_rating.toFixed(2) : "—"}
                        </div>
                    </div>
                </div>
                <div className="bg-white px-6 py-3 border-t border-[#D6E6F2] flex flex-wrap items-center gap-6">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0]">Look-fors rated</div>
                        <div className="font-serif text-xl font-semibold text-[#0B2545]">{data.rated_count}/{data.total_look_fors}</div>
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0]">Status</div>
                        <div className="font-serif text-xl font-semibold text-[#0B2545] capitalize">{data.status.replace(/_/g, " ")}</div>
                    </div>
                    <div className="text-xs text-[#0E1B2C]/60 ml-auto">
                        {saving ? <span className="text-amber-700 inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" />Saving…</span>
                            : lastSavedAt ? <span className="text-emerald-700 inline-flex items-center gap-1"><Check size={12} />Saved</span>
                            : "Auto-saves as you type"}
                    </div>
                    <Button
                        className="rounded-full text-white ml-auto"
                        style={{ backgroundColor: INST_COLOR }}
                        onClick={complete}
                        disabled={!data.writable || data.rated_count < data.total_look_fors || data.status === "complete"}
                        data-testid="ta-complete-btn"
                    >
                        <ClipboardCheck size={14} />
                        {data.status === "complete" ? "Complete" : "Mark complete"}
                    </Button>
                </div>
            </div>

            {!data.writable && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2 text-sm flex items-center gap-2">
                    {data.cycle.status === "locked" ? <Lock size={14} /> : <AlertCircle size={14} />}
                    {data.cycle.status === "locked" ? "Cycle is locked — read-only." : "You have read-only access to this teacher assessment."}
                </div>
            )}

            <div className="space-y-4">
                {data.indicators.map((ind) => (
                    <div key={ind.id} className="rounded-2xl bg-white border border-[#D6E6F2] overflow-hidden" data-testid={`ta-indicator-${ind.position}`}>
                        <div className="px-5 py-3 border-b border-[#D6E6F2] flex flex-wrap items-center justify-between gap-3" style={{ background: `${INST_COLOR}0d` }}>
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.25em] font-bold" style={{ color: INST_COLOR }}>
                                    Indicator {ind.position}
                                </div>
                                <div className="font-serif text-lg text-[#0B2545] font-semibold">{ind.name}</div>
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.25em] text-[#1E56A0] font-bold">Score</div>
                                <div className="font-serif text-xl font-semibold text-[#0B2545]">
                                    {ind.indicator_score != null ? ind.indicator_score.toFixed(2) : "—"}
                                </div>
                            </div>
                        </div>
                        <div className="divide-y divide-[#D6E6F2]">
                            {ind.look_fors.map((lf) => (
                                <div key={lf.id} className="px-5 py-3 grid grid-cols-1 lg:grid-cols-12 gap-4" data-testid={`ta-lookfor-${lf.id}`}>
                                    <div className="lg:col-span-5">
                                        <div className="font-semibold text-[#0B2545]">{lf.position}. {lf.name}</div>
                                        <div className="mt-1 text-sm text-[#0E1B2C]/70 leading-relaxed">{lf.description}</div>
                                    </div>
                                    <div className="lg:col-span-3">
                                        <RadioGroup
                                            className="flex items-center gap-3"
                                            value={lf.rating != null ? String(lf.rating) : ""}
                                            onValueChange={(v) => updateLF(lf, { rating: parseInt(v, 10) })}
                                            disabled={!data.writable}
                                        >
                                            {[1, 2, 3, 4].map((n) => (
                                                <label key={n} htmlFor={`${lf.id}-r${n}`} className={`flex items-center gap-1 cursor-pointer text-sm ${!data.writable ? "opacity-60 cursor-not-allowed" : ""}`} data-testid={`ta-rating-${lf.id}-${n}`}>
                                                    <RadioGroupItem id={`${lf.id}-r${n}`} value={String(n)} disabled={!data.writable} />
                                                    {n}
                                                </label>
                                            ))}
                                        </RadioGroup>
                                    </div>
                                    <div className="lg:col-span-4">
                                        <Textarea
                                            className="min-h-[54px] bg-[#F8FBFF] border-[#D6E6F2]"
                                            placeholder="Notes"
                                            value={lf.notes || ""}
                                            onChange={(e) => updateLF(lf, { notes: e.target.value })}
                                            disabled={!data.writable}
                                            data-testid={`ta-notes-${lf.id}`}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
